export const createPhoneMask = function (string) {
  // Remove existing formatting first
  const cleaned = string.replace(/\D/g, '');

  // If it's a North American number (starts with 1 and has 10 or 11 digits)
  if (
    (cleaned.length === 10 && /^[2-9]/.test(cleaned)) ||
    (cleaned.length === 11 && /^1[2-9]/.test(cleaned))
  ) {
    // Format as North American
    const match = cleaned.match(/^(\d{1})?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return match[1]
        ? `+${match[1]} ${match[2]}-${match[3]}-${match[4]}`
        : `+1 ${match[2]}-${match[3]}-${match[4]}`;
    }
  }

  // For international numbers, just add the plus sign and preserve digits
  return `+${cleaned}`;
};

export const destroyPhoneMask = function (string) {
  // Return only digits from the phone number
  return string.replace(/\D/g, '');
};

// Standardize phone number to E.164 format for API submission
export const standardizePhoneNumber = function (string) {
  if (!string) return null;

  // Remove all non-digits
  const digits = string.replace(/\D/g, '');

  // If it starts with a + sign, preserve that in the output
  if (string.startsWith('+')) {
    return `+${digits}`;
  }

  // Add default +1 code for 10-digit North American numbers
  if (digits.length === 10 && /^[2-9]/.test(digits)) {
    return `+1${digits}`;
  }

  // Otherwise just return with a + prefix
  return `+${digits}`;
};

export const formatMoney = function (value) {
  let val = (value / 1).toFixed(2);
  return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const paramArray = function (name, items) {
  const params = {};
  for (let i = 0; i < items.length; i++) {
    params[`${name}[${i}]`] = `${items[i]}`;
  }
  return params;
};

export const addToCart = function (product, callback = null) {
  this.$store.commit('addProductToCart', product);
  if (callback) callback();
};

export const removeFromCart = function (product) {
  this.$store.commit('removeProductFromCart', product);
};

export const removeCart = function () {
  this.$store.commit('removeProductsFromCart');
};

export const subtractProductQty = function (product) {
  // Create a clone to avoid reference issues
  const updatedProduct = { ...product };
  if (updatedProduct.quantityOrdered > 1) {
    updatedProduct.quantityOrdered--;
  }
  return updatedProduct;
};

export const addProductQty = function (product) {
  // Create a clone to avoid reference issues
  const updatedProduct = { ...product };
  if (updatedProduct.quantityOrdered < (updatedProduct.quantity || Number.MAX_SAFE_INTEGER)) {
    updatedProduct.quantityOrdered++;
  }
  return updatedProduct;
};

export const roundToTwoDecimals = function (num) {
  return parseFloat(`${num}`).toFixed(2);
};

export const bankersRound = function (n, d = 2) {
  var x = n * Math.pow(10, d);
  var r = Math.round(x);
  var br = Math.abs(x) % 1 === 0.5 ? (r % 2 === 0 ? r : r - 1) : r;
  return br / Math.pow(10, d);
};

// Order Conditions
export const minimumSubtotalCheck = function (vendor, quote) {
  if (vendor && quote && vendor.minimum_order_subtotal) {
    const sub =
      typeof quote.subtotal === 'string'
        ? quote.subtotal.replace(',', '')
        : quote.subtotal;

    return parseFloat(vendor.minimum_order_subtotal) <= parseFloat(sub);
  } else {
    return true;
  }
};

export const maximumOrderWeightCheck = function (region, cartProducts) {
  // Weight feature has been removed, always return true
  return true;
};

export const getIngredientMacros = function (
  ingredient,
  amount,
  portion = null
) {
  let grams =
    (portion ? portion.gram_weight : ingredient.serving_size) * amount;

  return {
    calories: Math.round(
      (grams / ingredient.serving_size) * ingredient.calories
    ),
    proteins: Math.round(
      (grams / ingredient.serving_size) * ingredient.proteins
    ),
    carbs: Math.round((grams / ingredient.serving_size) * ingredient.carbs),
    fats: Math.round((grams / ingredient.serving_size) * ingredient.fats),
    sugars: Math.round((grams / ingredient.serving_size) * ingredient.sugars),
    fibers: Math.round((grams / ingredient.serving_size) * ingredient.fibers),
  };
};

export const getIngredientsMacros = function (ingredients) {
  let macros = {
    calories: 0,
    proteins: 0,
    carbs: 0,
    fats: 0,
    sugars: 0,
    fibers: 0,
  };

  ingredients.forEach((i) => {
    if (!i.portion) return;

    let ingredientMacros = getIngredientMacros(
      i.ingredient,
      i.amount,
      i.portion
    );

    macros.calories = macros.calories + ingredientMacros.calories;
    macros.proteins = macros.proteins + ingredientMacros.proteins;
    macros.carbs = macros.carbs + ingredientMacros.carbs;
    macros.fats = macros.fats + ingredientMacros.fats;
    macros.sugars = macros.sugars + ingredientMacros.sugars;
    macros.fibers = macros.fibers + ingredientMacros.fibers;
  });

  return macros;
};

export const getModifierItemMacros = function (item, item_quantity = null) {
  let macros = {
    calories: 0,
    proteins: 0,
    carbs: 0,
    fats: 0,
    sugars: 0,
    fibers: 0,
  };

  if (item.custom_macros && item.item_quantity) {
    macros.calories = item.custom_macros.calories * item.item_quantity;
    macros.proteins = item.custom_macros.proteins * item.item_quantity;
    macros.carbs = item.custom_macros.carbs * item.item_quantity;
    macros.fats = item.custom_macros.fats * item.item_quantity;
    macros.sugars = item.custom_macros.sugars * item.item_quantity;
    macros.fibers = item.custom_macros.fibers * item.item_quantity;
  } else {
    let ingredients = [];

    // todo: make recursive
    if (item.recipes && item.recipes.length > 0) {
      item.recipes.forEach((r) =>
        r.ingredients.forEach((ri) => ingredients.push(ri))
      );
    }

    if (
      (item.ingredients && item.ingredients.length > 0) ||
      ingredients.length > 0
    ) {
      ingredients.concat(item.ingredients).forEach((mi) => {
        if (!mi.portion || !mi.portion) return;

        let ingredientMacros = getIngredientMacros(
          mi.ingredient,
          mi.amount,
          mi.portion
        );

        let quantity = item.item_quantity ?? item_quantity ?? 0;

        macros.calories =
          macros.calories + ingredientMacros.calories * quantity;
        macros.proteins =
          macros.proteins + ingredientMacros.proteins * quantity;
        macros.carbs = macros.carbs + ingredientMacros.carbs * quantity;
        macros.fats = macros.fats + ingredientMacros.fats * quantity;
        macros.sugars = macros.sugars + ingredientMacros.sugars * quantity;
        macros.fibers = macros.fibers + ingredientMacros.fibers * quantity;
      });
    }
  }
  return macros;
};

export const getProductMacros = function (
  product,
  consider_mods = true,
  default_mod_quantity = 0
) {
  let macros = [];
  if (product.custom_macros) {
    macros.push(product.custom_macros);
  } else if (product.ingredients) {
    product.ingredients.forEach((pi) => {
      macros.push(getIngredientMacros(pi.ingredient, pi.amount, pi.portion));
    });
  }

  // todo: make recursive
  const recipeMacros = (recipes) => {
    recipes.forEach((r) => {
      if (r.all_related_recipes) {
        recipeMacros(r.all_related_recipes);
      } else {
        return r.ingredients.forEach((ri) => {
          macros.push(
            getIngredientMacros(ri.ingredient, ri.amount, ri.portion)
          );
        });
      }
    });
  };

  if (product.recipes) {
    recipeMacros(product.recipes);
  }

  if (consider_mods && product.modifiers)
    product.modifiers.forEach((x) => {
      x.items.forEach((item) => {
        const itemMacros = getModifierItemMacros(
          item,
          item.default_quantity ?? default_mod_quantity
        );
        macros.push(itemMacros);
      });
    });

  return macros.reduce(
    function (accumulator, item) {
      accumulator.calories = accumulator.calories + item.calories;
      accumulator.proteins = accumulator.proteins + item.proteins;
      accumulator.carbs = accumulator.carbs + item.carbs;
      accumulator.fats = accumulator.fats + item.fats;
      accumulator.sugars = accumulator.sugars + item.sugars;
      accumulator.fibers = accumulator.fibers + item.fibers;
      return accumulator;
    },
    {
      calories: 0,
      proteins: 0,
      carbs: 0,
      fats: 0,
      sugars: 0,
      fibers: 0,
    }
  );
};

export const imagePlaceholder = function (str) {
  if (!str) return '';
  const words = str.trim().replace(/\W/g, '').split(' ');
  const wordsStr = words.reduce(
    (response, word) => (response += word.slice(0, 1)),
    ' '
  );
  if (wordsStr.length > 2) {
    return wordsStr.slice(0, 2).toUpperCase();
  }
  return wordsStr.toUpperCase();
};
